import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import FireCodeWorkflowViewer from "@/firecode/FireCodeWorkflowViewer.jsx";
import { chapters } from "@/firecode/data";
import { diagrams } from "@/firecode/diagrams";

function setMeta(name: string, content: string) {
  let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = name;
    document.head.appendChild(tag);
  }
  tag.content = content;
}

export default function FireCodePage() {
  const navigate = useNavigate();
  const location = useLocation();

  const viewerRouter = useMemo(
    () => ({
      getPath: () => {
        const path = window.location.pathname.replace(/\/+$/, "");
        if (path === "/fire-code") return "/";
        return path.startsWith("/fire-code/") ? path.slice("/fire-code".length) : path || "/";
      },
      push: (fullPath: string, options: { replace?: boolean } = {}) => {
        navigate(fullPath, { replace: Boolean(options.replace) });
      },
      subscribe: () => () => {},
    }),
    [navigate]
  );

  useEffect(() => {
    document.title = "SCDF Fire Code 2023 Workflows | Fire Safety Singapore";
    setMeta(
      "description",
      "Navigate SCDF Fire Code 2023 requirements by chapter, clause and decision workflow for Singapore fire safety compliance."
    );
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="pt-28 md:pt-32 pb-8 md:pb-10 px-4 md:px-6 lg:px-10 bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto">
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li>
                  <Link to="/resources" className="hover:text-primary-600 transition-colors">Resources</Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-900 font-medium">SCDF Fire Code 2023 Workflows</li>
              </ol>
            </nav>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-10 items-end">
              <div>
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
                  Digital Compliance Tool
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                  SCDF Fire Code 2023 <span className="text-primary-500">Decision Workflows</span>
                </h1>
                <p className="text-foreground-600 text-base md:text-lg max-w-3xl leading-relaxed">
                  Search by clause, browse all 11 chapters, and use structured decision trees to understand the compliance path before confirming with the official SCDF code and your Qualified Person.
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-accent-600 mb-2">
                  Beta Navigation Aid
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  This viewer is an interpretive workflow tool. It supports fire safety planning, but it does not replace the official Fire Code, SCDF circulars, or professional judgement.
                </p>
                <Link
                  to="/resources/scdf-fire-code-2023-digital-workflows"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Read the launch note
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <FireCodeWorkflowViewer
          key={location.pathname}
          chapters={chapters}
          diagrams={diagrams}
          basePath="/fire-code"
          router={viewerRouter}
          onRouteChange={(route: { view?: string; chapter?: number; slug?: string; query?: string }) => {
            if (route.view === "section" && route.chapter && route.slug) {
              document.title = `SCDF Fire Code 2023 Chapter ${route.chapter} | ${route.slug.replace(/-/g, " ")}`;
            } else if (route.view === "chapter" && route.chapter) {
              document.title = `SCDF Fire Code 2023 Chapter ${route.chapter} | Fire Safety Singapore`;
            } else if (route.view === "search" && route.query) {
              document.title = `SCDF Fire Code Search: ${route.query} | Fire Safety Singapore`;
            }
          }}
        />
      </main>
      <Footer />
    </div>
  );
}
